/**
 * LoadingState Component
 * Comprehensive loading states with spinner, skeleton, and progress variants
 */
import React from 'react';
import { LoadingSpinner } from './LoadingSpinner';

export interface LoadingStateProps {
  type?: 'spinner' | 'skeleton' | 'progress';
  message?: string;
  fullScreen?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export interface SkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'rectangular',
  width,
  height,
  className = '',
}) => {
  const baseClasses = 'animate-pulse bg-slate-200 dark:bg-slate-700 rounded';
  
  const variantClasses = {
    text: 'h-4',
    circular: 'rounded-full',
    rectangular: 'rounded',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
};

export const LoadingState: React.FC<LoadingStateProps> = ({
  type = 'spinner',
  message,
  fullScreen = false,
  size = 'md',
  className = '',
}) => {
  const content = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {type === 'spinner' && (
        <>
          <LoadingSpinner size={size} />
          {message && (
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">{message}</p>
          )}
        </>
      )}
      {type === 'skeleton' && (
        <div className="w-full space-y-4">
          {message && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{message}</p>
          )}
          {/* Default skeleton layout */}
          <Skeleton variant="rectangular" height={40} className="w-full" />
          <Skeleton variant="rectangular" height={40} className="w-full" />
          <Skeleton variant="rectangular" height={40} className="w-full" />
          <Skeleton variant="rectangular" height={40} className="w-3/4" />
        </div>
      )}
      {type === 'progress' && (
        <div className="w-full max-w-md">
          {message && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{message}</p>
          )}
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div className="bg-brand-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center">
        {content}
      </div>
    );
  }

  return content;
};

